import { Directive, ElementRef, effect, inject } from '@angular/core';
import { GuideEngineService } from '../services/guide-engine.service';

/**
 * Keeps the host element in the browser **top layer** (via the Popover API) so
 * the guide renders above modal `<dialog>` drawers — e.g. the New Brief type
 * selector, opened with `dialog.showModal()`. Modal dialogs live in the top
 * layer, which sits above every normal stacking context regardless of
 * `z-index`, so `z-index` alone can never win; being in the top layer too is
 * the only reliable fix.
 *
 * Re-asserts on every step change: the top layer stacks in insertion order
 * (last in wins), so a drawer opened mid-tour would otherwise be placed above
 * us. Toggling the popover re-inserts the host at the top.
 *
 * The popover is **non-modal**, so it does NOT make the page/drawer `inert` —
 * combined with the host's own `pointer-events` rules, real clicks still reach
 * the UI beneath (needed for auto-clicks and user-confirmation steps).
 */
@Directive({
  selector: '[guideTopLayer]',
  host: { popover: 'manual' },
})
export class GuideTopLayerDirective {
  private readonly el = inject(ElementRef).nativeElement as HTMLElement;
  private readonly engine = inject(GuideEngineService);

  constructor() {
    effect(() => {
      // Track step + active state so we re-raise above any newly-opened drawer.
      this.engine.currentStep();
      this.engine.isActive();
      this.raise();
    });
  }

  private raise(): void {
    const el = this.el;
    if (!el?.isConnected || typeof el.showPopover !== 'function') {
      return;
    }
    try {
      if (el.matches(':popover-open')) {
        el.hidePopover();
      }
      el.showPopover();
    } catch {
      // Transient state / unsupported — safe to ignore.
    }
  }
}
